/**
 * Default filter/dialog options served immediately (and as a resilient
 * fallback if the constants query fails), then refined from the API in
 * useOrderConstants. Not test data — these keep filters and dialogs usable
 * before/without a successful constants fetch.
 */
import type { ConstantOption, OrderConstants } from './types';

export const DEFAULT_ORDER_STATES: OrderConstants['states'] = [
  { code: 'PENDING', displayName: 'Pending' },
  { code: 'PAID', displayName: 'Paid' },
  { code: 'CAPTURED', displayName: 'Captured' },
  { code: 'FULFILLING', displayName: 'Fulfilling' },
  { code: 'PICKING', displayName: 'Picking' },
  { code: 'PICKED', displayName: 'Picked' },
  { code: 'PACKING', displayName: 'Packing' },
  { code: 'PACKED', displayName: 'Packed' },
  { code: 'SHIPPED', displayName: 'Shipped' },
  { code: 'DELIVERED', displayName: 'Delivered' },
  { code: 'FLAGGED', displayName: 'Flagged' },
  { code: 'CANCELED', displayName: 'Canceled' },
  { code: 'PAYMENT_FAILED', displayName: 'Payment Failed' },
];
export const DEFAULT_YPACKAGE_STATES: OrderConstants['ypackageStates'] = [
  { code: 'CREATED', displayName: 'Created' },
  { code: 'PACKING', displayName: 'Packing' },
  { code: 'PACKED', displayName: 'Packed' },
  { code: 'SHIPPED', displayName: 'Shipped' },
  { code: 'DELIVERED', displayName: 'Delivered' },
  { code: 'RETURNED', displayName: 'Returned' },
  { code: 'LOST_IN_TRANSIT', displayName: 'Lost in Transit' },
  { code: 'CANCELED', displayName: 'Canceled' },
];
export const DEFAULT_CONDITIONS: OrderConstants['conditions'] = [
  { code: 'GRADE_A', displayName: 'Like New' },
  { code: 'GRADE_B', displayName: 'Excellent' },
  { code: 'GRADE_C', displayName: 'Good' },
  { code: 'GRADE_D', displayName: 'Fair' },
];
export const DEFAULT_REFUND_REASONS: OrderConstants['refundReasons'] = [
  { code: 'WRONG_CONDITION', displayName: 'Item condition' },
  { code: 'WRONG_SIZE', displayName: 'Wrong size' },
  { code: 'SHIPPING_ISSUE', displayName: 'Shipping issue' },
  { code: 'CS_ISSUE', displayName: 'Customer service issue' },
  { code: 'DISSATISFIED', displayName: 'Dissatisfied with accurate condition item' },
  { code: 'OTHER', displayName: 'Other' },
];
export const DEFAULT_GIFTCARD_REASONS: OrderConstants['giftcardReasons'] = [
  { code: 'CANNOT_REFUND', displayName: 'Cannot refund' },
  { code: 'SHIPPING_ISSUE', displayName: 'Shipping issue' },
  { code: 'GOODWILL', displayName: 'Goodwill gesture' },
  { code: 'OTHER', displayName: 'Other' },
];

/**
 * Problem-order return reasons (reware-api lib/constants/returns.js `problemReturnReasons`).
 * This page always initiates returns with free return shipping, which is the problem-order flow,
 * so these rather than the customer-facing `returnReasons`.
 */
export const DEFAULT_RETURN_REASONS: ConstantOption[] = [
  { code: 'WRONG_CONDITION', displayName: 'Wrong condition' },
  { code: 'WRONG_SIZE', displayName: 'Wrong size' },
  { code: 'WRONG_ITEM', displayName: 'Wrong item' },
  { code: 'WRONG_COLOR', displayName: 'Wrong color' },
  { code: 'MISSING_PARTS', displayName: 'Missing parts' },
  { code: 'SECURITY_TAG', displayName: 'Security tag' },
  { code: 'ORDER_SWAP', displayName: 'Order swap' },
  { code: 'COUNTERFEIT_ITEM', displayName: 'Counterfeit items' },
  { code: 'ITEM_NOT_AS_EXPECTED', displayName: 'Item not as expected' },
  { code: 'FREE_SHIPPING_APPEASEMENT', displayName: 'Free shipping appeasement' },
];

/** reware-api lib/constants/discounts.js `FREE_SHIPPING_REASONS` + OTHER. */
export const DEFAULT_FREE_SHIPPING_REASONS: ConstantOption[] = [
  { code: 'WRONG_CONDITION', displayName: 'Item condition' },
  { code: 'WRONG_SIZE', displayName: 'Wrong size' },
  { code: 'WRONG_COLOR', displayName: 'Wrong color' },
  { code: 'WRONG_ITEM', displayName: 'Wrong item altogether' },
  { code: 'SHIPPING_ISSUE', displayName: 'Shipping Issue' },
  { code: 'CS_ISSUE', displayName: 'Customer Service Issue' },
  { code: 'TECHNICAL_ISSUE', displayName: 'Technical Issue' },
  { code: 'INFLUENCER_ORDER', displayName: 'Influencer Order' },
  { code: 'PROMOTION', displayName: 'Promotion' },
  { code: 'DISSATISFIED', displayName: 'Dissatisfied with accurate condition item' },
  { code: 'EMPLOYEE_ORDER', displayName: 'Employee Order' },
  { code: 'COMBINED_ORDER', displayName: 'Combined Orders' },
  { code: 'FULFILLMENT_DELAY', displayName: 'Fulfillment Delay' },
  { code: 'OTHER', displayName: 'Other' },
];
